import React, { ComponentType, SVGProps, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { JSONContent } from "@tiptap/core/dist/types";
import { SimpleEditor } from "../@/components/tiptap-templates/simple/simple-editor";
import LoadingIndicator from "../components/LoadingIndicator";
import { Button } from "../@/components/tiptap-ui-primitive/button/button";
import StatusPill, { UpdateStatus } from "../components/StatusPill";
import {
  FaSave as RawFaSave,
  FaEye as RawFaEye,
  FaPaperPlane as RawFaPaperPlane,
  FaArrowLeft as RawFaArrowLeft,
} from "react-icons/fa";

const FaArrowLeft = RawFaArrowLeft as ComponentType<SVGProps<SVGSVGElement>>;
const FaSave = RawFaSave as ComponentType<SVGProps<SVGSVGElement>>;
const FaEye = RawFaEye as ComponentType<SVGProps<SVGSVGElement>>;
const FaPaperPlane = RawFaPaperPlane as ComponentType<SVGProps<SVGSVGElement>>;

interface EmailUpdate {
  id: string;
  title: string;
  status: UpdateStatus;
  lastSentAt: string | null;
  body: JSONContent;
}

interface EmailUpdateRecipient {
  id: string;
  email: string;
  name: string | null;
  sentAt: string | null;
}

const DUMMY_UPDATE_ID = "123"; // Dummy ID for initial load

const Update: React.FC = () => {
  const navigate = useNavigate();

  // State variables
  const [loading, setLoading] = useState<boolean>(true);
  const [update, setUpdate] = useState<EmailUpdate | null>(null);
  const [editedBody, setEditedBody] = useState<JSONContent | null>(null);
  const [status, setStatus] = useState<UpdateStatus>("edit");
  const [recipients, setRecipients] = useState<EmailUpdateRecipient[]>([]);
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch email update data and recipients (dummy API calls here)
  useEffect(() => {
    const fetchEmailUpdate = async () => {
      setLoading(true);
      setError(null);
      try {
        // Simulated API call to fetch update by ID
        // Replace with real API calls:
        // const { data } = await client.get(`/email_updates/${DUMMY_UPDATE_ID}`);
        // const recipientsData = await client.get(`/email_update_recipients?updateId=${DUMMY_UPDATE_ID}`);

        // Dummy data simulating API response
        const data: EmailUpdate = {
          id: DUMMY_UPDATE_ID,
          title: "April Product Update",
          status: "edit",
          lastSentAt: null,
          body: {
            type: "doc",
            content: [
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: "Welcome to the April update! Here's what we've been working on...",
                  },
                ],
              },
            ],
          },
        };

        const recipientsData: EmailUpdateRecipient[] = [
          { id: "r1", email: "alice@example.com", name: "Alice", sentAt: null },
          { id: "r2", email: "bob@example.com", name: "Bob", sentAt: null },
          { id: "r3", email: "carol@example.com", name: null, sentAt: null },
        ];

        setUpdate(data);
        setEditedBody(data.body);
        setStatus(data.status);
        setRecipients(recipientsData);
      } catch (err) {
        setError("Failed to load update data.");
      } finally {
        setLoading(false);
      }
    };

    fetchEmailUpdate();
  }, []);

  // Save the edited content (dummy)
  const saveUpdate = async () => {
    if (!update || !editedBody) return;
    setLoading(true);
    setError(null);
    try {
      // dummy API call: await client.put(`/email_updates/${update.id}`, { body: editedBody });
      // Simulate update saved
      setUpdate(prev => (prev ? { ...prev, body: editedBody } : prev));
      alert("Update saved successfully.");
    } catch (err) {
      setError("Failed to save update.");
    } finally {
      setLoading(false);
    }
  };

  // Preview button action
  const onPreview = () => {
    setStatus("preview");
  };

  // Prepare and send button action
  const onSend = async () => {
    if (!update) return;
    setSending(true);
    setError(null);
    try {
      // Dummy sending simulation
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Simulate API call to mark recipients as sent
      const updatedRecipients = recipients.map(r => ({ ...r, sentAt: new Date().toISOString() }));
      setRecipients(updatedRecipients);

      // Simulate API call to update update.status and lastSentAt
      setUpdate(prev => {
        if (!prev) return prev;
        return { ...prev, status: "sent", lastSentAt: new Date().toISOString() };
      });
      setStatus("sent");
      alert("Email update has been sent to recipients.");
    } catch (err) {
      setError("Failed to send update.");
    } finally {
      setSending(false);
    }
  };

  // Edit button action from preview
  const onEdit = () => {
    setStatus("edit");
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!update) {
    return <p className="p-6">No update found.</p>;
  }

  return (
    <main className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4 text-center">{update.title}</h1>

      {/* Layout the status pill horizontally aligned with the buttons, pill left aligned, buttons right aligned */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center">
          <StatusPill status={status} />
        </div>

        <div className="flex items-center flex-wrap gap-2">
          {/* Save button is always displayed, disabled if loading or sending */}
          <Button
            data-style="ghost"
            onClick={saveUpdate}
            disabled={loading || sending}
            aria-label="Save update"
            className="flex items-center gap-2"
          >
            <FaSave /> Save
          </Button>

          {/* Buttons rendered according to status */}
          {status === "edit" && (
            <Button
              data-style="ghost"
              onClick={onPreview}
              disabled={loading || sending}
              aria-label="Preview update"
              className="flex items-center gap-2"
            >
              <FaEye /> Preview
            </Button>
          )}

          {status === "preview" && (
            <>
              <Button
                data-style="ghost"
                onClick={onSend}
                disabled={loading || sending}
                aria-label="Send update"
                className="flex items-center gap-2"
              >
                <FaPaperPlane /> Send
              </Button>

              <Button
                data-style="ghost"
                onClick={onEdit}
                disabled={loading || sending}
                aria-label="Edit update"
                className="flex items-center gap-2"
              >
                Edit
              </Button>
            </>
          )}

          {status === "sent" && (
            <span className="inline-flex items-center gap-1 text-green-600 font-semibold" aria-label="Sent">
              Sent
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </span>
          )}
        </div>
      </div>

      {(status === "edit" || status === "prepare") && editedBody && (
        <div className="mb-6 border p-4 rounded bg-white">
          <SimpleEditor
            initialContent={editedBody}
            onChange={(content) => setEditedBody(content)}
          />
        </div>
      )}

      {status === "preview" && editedBody && (
        <div className="prose border p-4 rounded mb-6">
          {/* Simple preview rendering text only from raw JSONContent for demonstration */}
          {editedBody.content &&
            editedBody.content.map((node, idx) => {
              if (node.type === "paragraph" && node.content) {
                return (
                  <p key={idx}>
                    {node.content.map((child, cidx) => {
                      if (child.type === "text") return child.text;
                      return null;
                    })}
                  </p>
                );
              }
              return null;
            })}
        </div>
      )}

      {status === "sent" && (
        <div className="mb-6 text-green-800 font-semibold">
          This update was sent on {update.lastSentAt ? new Date(update.lastSentAt).toLocaleString() : "-"}.
        </div>
      )}

      {status === "prepare" && recipients.length > 0 && (
        <div className="mb-6">
          <h2 className="font-semibold mb-2">Recipients ({recipients.length})</h2>
          <ul className="border rounded max-h-48 overflow-auto p-2">
            {recipients.map(({ id, email, name, sentAt }) => (
              <li key={id} className="text-sm border-b last:border-b-0 py-1">
                <b>{name || "(No Name)"}</b> &lt;{email}&gt; {sentAt ? "(Sent)" : "(Pending)"}
              </li>
            ))}
          </ul>
        </div>
      )}
    </main>
  );
};

export default Update;
